import { FallbackProps } from "react-error-boundary";
import { ReactElement } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Container,
  Heading,
} from "@chakra-ui/react";
import { useModifiedTranslation } from "./hooks/useModifiedTranslation";
import { DB } from "./utils/db";
import Dexie from "dexie";

export function ErrorFallback({
  error,
  resetErrorBoundary,
}: FallbackProps): ReactElement {
  const t = useModifiedTranslation();

  const handleReset = () => {
    const isDexieError = error instanceof Dexie.DexieError;

    if (!isDexieError) {
      // IndexedDBが絡まないエラーは画面リセット
      resetErrorBoundary();
      return;
    }

    const needsDbResetErrors = ["VersionError", "SchemaError"];
    const needsDbReset =
      needsDbResetErrors.includes(error.inner?.name ?? "") ||
      needsDbResetErrors.includes(error.name);

    if (needsDbReset) {
      console.warn("Database error detected, resetting database:", error);
      DB.resetDatabase().finally(() => {
        resetErrorBoundary();
      });
      return;
    }

    // その他の条件なら画面リセット
    resetErrorBoundary();
  };

  return (
    <Container py={12}>
      <Heading as="h1" textAlign="center" mb={5}>
        Unexpected Error
      </Heading>

      <Alert
        status="error"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={8}
      >
        <AlertIcon boxSize="40px" mr={0} />
        <AlertTitle mt={4} mb={1} fontSize="lg">
          {error.name}
        </AlertTitle>
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>

      <Button
        width="full"
        variant="primary.theme_lv1"
        my={10}
        onClick={handleReset}
      >
        {t("ErrorFallback.reset")}
      </Button>
    </Container>
  );
}
